import React, { useState, useCallback } from 'react';
import { BUTTON_COLORS } from './buttonColors';

/**
 * Custom confirmation dialog hook
 * Returns a confirmation dialog component and a function to show it
 */
export const useConfirmationDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState({
    title: 'Confirm Action',
    message: 'Are you sure?',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: () => {},
    onCancel: () => {},
    confirmButtonColor: BUTTON_COLORS.DANGER, // Default to red for destructive actions
    cancelButtonColor: BUTTON_COLORS.CANCEL, // Default to gray for cancel
  });

  const showConfirmation = useCallback((dialogConfig) => {
    setConfig({
      ...config,
      ...dialogConfig,
    });
    setIsOpen(true);
  }, [config]);

  const hideConfirmation = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleConfirm = useCallback(() => {
    config.onConfirm();
    hideConfirmation();
  }, [config, hideConfirmation]);

  const handleCancel = useCallback(() => {
    config.onCancel();
    hideConfirmation();
  }, [config, hideConfirmation]);

  const ConfirmationDialog = () => {
    if (!isOpen) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001,
      }}>
        <div className="confirmation-dialog" style={{
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '400px',
          width: '90%',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}>
          <h3 style={{
            margin: '0 0 16px 0',
            color: 'var(--color-text)',
            fontSize: '18px',
            fontWeight: '600',
          }}>
            {config.title}
          </h3>
          
          <p style={{
            margin: '0 0 24px 0',
            color: 'var(--color-text-muted)',
            fontSize: '14px',
            lineHeight: '1.5',
          }}>
            {config.message}
          </p>
          
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
          }}>
            <button
              type="button"
              onClick={handleCancel}
              className="btn"
              style={{
                backgroundColor: config.cancelButtonColor,
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: 'pointer',
                opacity: 1,
              }}
            >
              {config.cancelText}
            </button>
            
            <button
              type="button"
              onClick={handleConfirm}
              className="btn"
              style={{
                backgroundColor: config.confirmButtonColor,
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: 'pointer',
                opacity: 1,
              }}
            >
              {config.confirmText}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return {
    showConfirmation,
    ConfirmationDialog,
  };
};

/**
 * Shared dialog creation logic
 */
const createDialog = (title, message, options = {}) => {
  return new Promise((resolve) => {
    const dialog = document.createElement('div');
    dialog.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.75);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10001;
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      background-color: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: 8px;
      padding: 24px;
      max-width: 400px;
      width: 90%;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    `;

    const titleEl = document.createElement('h3');
    titleEl.textContent = title;
    titleEl.style.cssText = `
      margin: 0 0 16px 0;
      color: var(--color-text);
      font-size: 18px;
      font-weight: 600;
    `;

    const messageEl = document.createElement('p');
    messageEl.innerHTML = message;
    messageEl.style.cssText = `
      margin: 0 0 24px 0;
      color: var(--color-text-muted);
      font-size: 14px;
      line-height: 1.5;
    `;

    const buttonsContainer = document.createElement('div');
    buttonsContainer.style.cssText = `
      display: flex;
      gap: 12px;
      justify-content: flex-end;
    `;

    let isClosed = false;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        close(false);
      }
    };

    const close = (result) => {
      if (isClosed) return;
      isClosed = true;
      try {
        document.removeEventListener('keydown', handleKeyDown);
      } catch (_) {}
      try {
        if (dialog && dialog.parentNode) {
          dialog.parentNode.removeChild(dialog);
        }
      } catch (e) {
        console.warn('Error removing dialog:', e);
      }
      try {
        resolve(result);
      } catch (_) {}
    };

    const confirmButton = document.createElement('button');
    confirmButton.textContent = options.confirmText || 'Confirm';
    confirmButton.style.cssText = `
      background-color: ${options.confirmButtonColor || BUTTON_COLORS.DANGER};
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      font-size: 14px;
      cursor: pointer;
    `;
    confirmButton.onclick = () => close(true);

    // Only add cancel button if cancelText is provided and not empty
    if (options.cancelText && options.cancelText.trim() !== '') {
      const cancelButton = document.createElement('button');
      cancelButton.textContent = options.cancelText;
      cancelButton.style.cssText = `
        background-color: ${options.cancelButtonColor || BUTTON_COLORS.CANCEL};
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        font-size: 14px;
        cursor: pointer;
      `;
      cancelButton.onclick = () => close(false);
      buttonsContainer.appendChild(cancelButton);
    }

    buttonsContainer.appendChild(confirmButton);

    content.appendChild(titleEl);
    content.appendChild(messageEl);
    content.appendChild(buttonsContainer);
    dialog.appendChild(content);
    document.body.appendChild(dialog);

    document.addEventListener('keydown', handleKeyDown);
  });
};

/**
 * Simple confirmation function that returns a Promise
 * This can be used as a drop-in replacement for window.confirm()
 */
export const confirmAction = (title, message, options = {}) => {
  return createDialog(title, message, options);
};

/**
 * Simple acknowledgment function for single-button dialogs
 * Returns a Promise that always resolves to true
 */
export const acknowledgeAction = (title, message, options = {}) => {
  return createDialog(title, message, {
    confirmText: options.confirmText || 'OK',
    confirmButtonColor: options.confirmButtonColor || BUTTON_COLORS.PRIMARY,
    cancelText: '', // No cancel button
    ...options
  });
};
